import React from "react";

/**
 * BrandLogo — renders the official Kandake or GFMP logo AS-IS (no recolor,
 * no stretch). Each variant ships with a backdrop tile that matches the
 * source image's native background so corners look intentional and the
 * artwork sits cleanly on whatever surface it's dropped onto.
 *
 *   <BrandLogo variant="kandake" size="md" />
 *   <BrandLogo variant="gfmp"    size="lg" />
 *
 * Sizes: "sm" (28px), "md" (48px), "lg" (96px), "xl" (160px), or pass
 *        a custom Tailwind class through `className`.
 *
 * `frame` controls the corner radius: "square" (no rounding), "rounded"
 *        (rounded-md, default), or "circle" (full circle — only flatters
 *        the Kandake variant; the GFMP variant looks better as a square).
 */

const SIZE_CLASS = {
  xs: "w-6 h-6",
  sm: "w-8 h-8",
  md: "w-12 h-12",
  lg: "w-24 h-24",
  xl: "w-40 h-40",
  "2xl": "w-56 h-56",
};

const VARIANT = {
  kandake: {
    src: "/brand/kandake.png",
    alt: "Kandake",
    // Source image is on a solid black backdrop with a deep-red sunburst.
    // Use the native black so the rounded container blends with the image.
    backdrop: "#000000",
    testId: "brand-logo-kandake",
  },
  "kandake-gold": {
    // Light-safe Kandake mark: gold/amber sunburst on TRANSPARENT canvas.
    // Set backdrop to "transparent" so the parent surface (cream / burgundy /
    // black) shows through and only the gold mark is visible. Use this variant
    // wherever the dark-on-dark Kandake variant disappears against its panel.
    src: "/brand/kandake-gold.png",
    alt: "Kandake",
    backdrop: "transparent",
    testId: "brand-logo-kandake-gold",
  },
  gfmp: {
    src: "/brand/gfmp.png",
    alt: "Good Friar Music Publishing",
    // Source image is on a deep burgundy with a gold ornate monogram.
    // The hex is sampled to match the asset's burgundy so the seam is invisible.
    backdrop: "#4A0F0E",
    testId: "brand-logo-gfmp",
  },
};

export default function BrandLogo({
  variant = "kandake",
  size = "md",
  frame = "rounded",
  className = "",
  imgClassName = "",
  withRing = false,
  ariaHidden = false,
  testId,
}) {
  const v = VARIANT[variant] || VARIANT.kandake;
  const sizeClass = SIZE_CLASS[size] || size; // allow raw tailwind class as size

  const radiusClass =
    frame === "square"
      ? "rounded-none"
      : frame === "circle"
      ? "rounded-full"
      : "rounded-md";

  const ringClass = withRing
    ? "ring-1 ring-[hsl(40_48%_45%/0.35)] shadow-[0_2px_10px_-3px_rgba(0,0,0,0.4)]"
    : "";

  return (
    <span
      className={`inline-block overflow-hidden shrink-0 ${sizeClass} ${radiusClass} ${ringClass} ${className}`}
      style={{ background: v.backdrop }}
      data-testid={testId || v.testId}
      aria-hidden={ariaHidden ? "true" : undefined}
      role={ariaHidden ? undefined : "img"}
      aria-label={ariaHidden ? undefined : v.alt}
    >
      <img
        src={v.src}
        alt={ariaHidden ? "" : v.alt}
        draggable="false"
        className={`block w-full h-full object-contain select-none ${imgClassName}`}
      />
    </span>
  );
}
